/**
 * Pure helpers backing `CommentsProvider` (#2244).
 *
 * Split out of `comments-context.tsx` so neither file needs an
 * `eslint-disable max-lines` escape hatch — the provider owns the
 * React state and callbacks, this file owns the per-render filter &
 * text-range math that doesn't depend on React itself.
 */

import type { AxiosError } from 'axios';

import type { Comment } from '@utils/types/comments/comments.ts';
import type { BaseError } from '@utils/types/shared/base.ts';
import type {
  DocumentTranscriptUtteranceV1,
  DocumentTranscriptTextV1
} from '@utils/types/editors/transcripts/transcript.ts';
import type { CommentFilter } from './comments-filter-context.tsx';

export interface FilterFlags {
  showOpen: boolean;
  showResolved: boolean;
}

export function filterFlagsFor(filter: CommentFilter): FilterFlags {
  return {
    showOpen: filter === 'open' || filter === 'all',
    showResolved: filter === 'resolved' || filter === 'all'
  };
}

export function commentsOverlappingWord(
  comments: Comment[],
  leafStart: number | undefined,
  leafStop: number | undefined
): Comment[] {
  if (leafStart == null || leafStop == null) return [];
  return comments.filter(c => {
    const from = c.index_from ? Number(c.index_from) : NaN;
    const to = c.index_to ? Number(c.index_to) : NaN;
    if (!Number.isFinite(from) || !Number.isFinite(to)) return false;
    return leafStart <= to && leafStop >= from;
  });
}

export function commentsEndingAtStop(
  comments: Comment[],
  leafStop: number | undefined
): Comment[] {
  if (leafStop == null) return [];
  return comments.filter(c => {
    const to = c.index_to ? Number(c.index_to) : NaN;
    return Number.isFinite(to) && to === leafStop;
  });
}

/**
 * The text a comment is anchored to. Walks the utterances and joins
 * every leaf whose word timestamps overlap the given range.
 */
export function textForRange(
  utterances: DocumentTranscriptUtteranceV1[] | undefined,
  indexFrom: string | undefined,
  indexTo: string | undefined
): string {
  if (!utterances) return '';
  const from = Number(indexFrom);
  const to = Number(indexTo);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return '';
  const words: string[] = [];
  for (const utt of utterances) {
    for (const para of utt.children ?? []) {
      for (const leaf of (para.children ?? []) as DocumentTranscriptTextV1[]) {
        const s = typeof leaf.start === 'number' ? leaf.start : NaN;
        const e = typeof leaf.stop === 'number' ? leaf.stop : NaN;
        if (!Number.isFinite(s) || !Number.isFinite(e)) continue;
        if (s <= to && e >= from && leaf.text) {
          words.push(leaf.text);
        }
      }
    }
  }
  return words.join('').replace(/\s+/g, ' ').trim();
}

export function errorMessage(err: unknown, fallback: string): string {
  const e = err as AxiosError<BaseError> | undefined;
  return (
    e?.response?.data?.error_description ??
    e?.response?.data?.detail?.toString() ??
    e?.response?.data?.error ??
    e?.response?.data?.message ??
    fallback
  );
}
